import { Alert, Snackbar } from "@mui/material";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

interface SnackbarContextType {
  showMessage: (
    message: ReactNode | string,
    severity?: "error" | "warning" | "info" | "success"
  ) => void;
  showUnhandledError: (message: string) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(
  undefined
);

export const SnackbarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState<{
    message: ReactNode | string;
    open: boolean;
    severity: "error" | "warning" | "info" | "success";
  }>({
    message: "",
    open: false,
    severity: "info",
  });

  const showMessage = useCallback(
    (
      message: ReactNode | string,
      severity: "error" | "warning" | "info" | "success" = "info"
    ) => {
      setSnackbar({ message, open: true, severity });
    },
    [setSnackbar]
  );

  const showUnhandledError = (message: ReactNode | string) => {
    if (typeof message === "string") {
      showMessage(
        t("alert_error.unhandled_error").replace("<error>", message),
        "error"
      );
    } else if (React.isValidElement(message)) {
      showMessage(message, "error");
    }
  };

  const handleClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <SnackbarContext.Provider value={{ showMessage, showUnhandledError }}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
